import angular from 'angular';
import gettext from '@sdk/lang/gettext';
import { TimesheetStatus } from '@sdk/timesheets/api/apiTypes';
import { isFeatureEnabled } from '@sdk/utils/isFeatureEnabled';

import { mapTimesheetHistory } from '@web/timeClock/TimesheetHistory/timesheetHistory.utils';

import config from '../../config';
import { LOGS_TYPES } from '../../shifts/constants';

import editTimesheetsComponentUrl from './editTimesheetsComponent.html';

class EditTimesheetsCtrl {
  TIME_CARD_NOTES_ENABLED = config.TIME_CARD_NOTES_ENABLED;
  NOTE_UPDATED = LOGS_TYPES.NOTE_UPDATED;
  PLACEHOLDER_TEXTS = {
    position: gettext('Add position'),
    location: gettext('Add location'),
    employee: gettext('Add employee'),
  };

  TimesheetStatus = TimesheetStatus;

  constructor(
    Authentication,
    TimesheetsService,
    TimesheetsUtils,
    EventCollection,
    PersonaCollection,
    Restangular,
    ShiftUtils,
    UserUtils,
    Utils,
    sdk,
    extendUserModel,
    focus,
    $timeout,
    AlertService
  ) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      TimesheetsService,
      TimesheetsUtils,
      EventCollection,
      PersonaCollection,
      Restangular,
      ShiftUtils,
      UserUtils,
      Utils,
      sdk,
      extendUserModel,
      focus,
      $timeout,
      AlertService,
    });
  }

  $onInit() {
    if (this.initialMode === 'history') {
      return this.showHistory();
    }

    this.TIMESHEET_ENTRY_TYPES = this.sdk('constants').TIMESHEET_ENTRY_TYPES;
    this.AUTOMATED_BREAKS_ENABLED = isFeatureEnabled(
      config.AUTOMATED_BREAKS_ENABLED,
      this.Authentication.user.org.id
    );

    if (this.event) {
      return this.initForEvent();
    }

    this.newTimesheet = {
      defaultDate: this.defaultDate,
    };
    this.users = this.PersonaCollection.users.filter((u) => u.timeclockEnabled);
    this.locationsForPicker = this.UserUtils.getLocationsForPersonaPicker();
    this.positionsForPicker = this.UserUtils.getPositionsForPersonaPicker();
    this.timesheetGroups = [];
    this.originalTimesheetGroups = angular.copy(this.timesheetGroups);
    this.addTimesheetGroup();
  }

  initForEvent() {
    const { MARK_APPROVED, MARK_PENDING } = this.TIMESHEET_ENTRY_TYPES;

    this.timesheetGroups = this.Utils.breakDownTimesheetsToGroups(this.event.timesheetEntries).map(
      (group) => ({
        timesheets: group.filter((e) => ![MARK_APPROVED, MARK_PENDING].includes(e.type)),
        allTimesheets: group,
        getData: null, // will be populated by editTimesheetGroupComponent
        isValid: () => true,
      })
    );
    this.originalTimesheetGroups = angular.copy(this.timesheetGroups);

    this.isBreakRelevant =
      Boolean(this.Authentication.user.orgSettings.shiftBreakType) && this.event.breakDuration;
    [this.editedWhen, this.editedBy] = this.event.timesheetEntries.reduce(
      (acc, timesheet) => {
        if (timesheet.edited && ![MARK_APPROVED, MARK_PENDING].includes(timesheet.type)) {
          const edited = this.Utils.getRealDate(timesheet.edited);
          return edited > acc[0] ? [edited, timesheet.actor] : acc;
        }

        return acc;
      },
      [0, null]
    );

    if (this.editedBy) {
      this.editedBy = this.extendUserModel(this.editedBy);
    }

    if (this.editedWhen) {
      this.editedWhenZone = this.sdk('localize').abbr(
        this.editedWhen,
        this.Authentication.user.timezone
      );
    }

    this.note = this.event ? this.event.assigneeNotes : '';
  }

  async submit() {
    if (this.editNoteModeOnly) {
      await this.addTimeclockNote();
      const updatedEvent = await this.EventCollection.getEvent(this.event.id, {
        includeTimesheets: 'full',
      });
      this.AlertService.success('Your note has been saved successfully.');
      this.modal.close(this.TimesheetsService.extendTimesheetModel(updatedEvent));
    } else {
      const payload = this.timesheetGroups.reduce((acc, group) => [...acc, ...group.getData()], []);

      const timesheets = await this.TimesheetsService.bulkUpdate(payload);
      const eventId = this.event?.id || timesheets[0].event.id;
      await this.addTimeclockNote();
      const updatedEvent = await this.EventCollection.getEvent(eventId, {
        includeTimesheets: 'full',
      });
      this.modal.close(this.TimesheetsService.extendTimesheetModel(updatedEvent));
    }
  }

  isLastVisible(group) {
    const nonDeleted = this.timesheetGroups.filter((g) => !g.deleted);
    return nonDeleted[nonDeleted.length - 1] === group;
  }

  async addTimesheetGroup() {
    this.timesheetGroups.push({
      allTimesheets: [],
      timesheets: [],
      getData: null,
      isValid: () => true,
    });

    if (this.form) {
      this.form.$setDirty();
    }

    await this.$timeout();
    this.focus(`clockIn-${this.timesheetGroups.length - 1}`);
  }

  removeTimesheetGroup(group) {
    group.deleted = true;

    if (!group.allTimesheets) {
      return (this.timesheetGroups = angular.copy(this.originalTimesheetGroups));
    }

    group.getData = () =>
      group.allTimesheets
        .map((timesheet) => {
          if (timesheet.id) {
            return {
              id: timesheet.id,
              deleted: true,
            };
          }

          return null;
        })
        .filter((a) => a);

    if (this.form) {
      this.form.$setDirty();
    }
  }

  areGroupsValid() {
    return (
      this.timesheetGroups.filter((group) => !group.deleted).every((group) => group.isValid()) &&
      this.groupsDoNotOverlap()
    );
  }

  groupsDoNotOverlap() {
    let prev;
    return this.timesheetGroups
      .filter((group) => !group.deleted)
      .sort((g1, g2) => g1.startDate - g2.startDate)
      .reduce((acc, group) => {
        const res = acc && (!prev || prev.endDate <= group.startDate);
        prev = group;
        return res;
      }, true);
  }

  get totalDuration() {
    return this.timesheetGroups.reduce(
      (prev, { deleted, startDate, endDate }) =>
        prev + (endDate && !deleted ? (endDate - startDate) / 1000 / 60 : 0),
      0
    );
  }

  get totalBreak() {
    return this.timesheetGroups.reduce(
      (prev, { deleted, totalBreak }) => prev + ((!deleted && totalBreak) || 0),
      0
    );
  }

  get actualDuration() {
    let unpaidBreak = this.totalBreak;
    if (this.Authentication.user.orgSettings.shiftBreakType === this.sdk('constants').PAID_BREAKS) {
      unpaidBreak = Math.max(0, unpaidBreak - (this.event?.breakDuration || 0));
    }

    return Math.max(0, this.totalDuration - unpaidBreak);
  }

  get noteVisible() {
    if (!this.event || !this.TIME_CARD_NOTES_ENABLED) {
      return false;
    }

    if (!this.note) {
      return this.editNoteModeOnly || (this.canEditNote && this.canEditTimesheet);
    }

    return true;
  }

  get canEditNote() {
    return this.event?.user.isMe && this.event?.timesheetStatus !== TimesheetStatus.approved;
  }

  get canEditTimesheet() {
    return this.event && this.TimesheetsUtils.canEditTimesheet(this.event);
  }

  async showHistory() {
    this.historyMode = true;
    this.historyItems = null;
    this.historyItems = mapTimesheetHistory(
      await this.TimesheetsService.getHistory(this.event.id),
      this.PersonaCollection.personasDict,
      this.Authentication.user,
      this.event.timesheetProjections
    );
  }

  newTimesheetUserChanged() {
    this.locationsForPicker = this.UserUtils.getLocationsForPersonaPicker(
      this.newTimesheet.user && [this.newTimesheet.user]
    );
    this.positionsForPicker = this.UserUtils.getPositionsForPersonaPicker(
      this.newTimesheet.user && [this.newTimesheet.user]
    );

    if (!this.newTimesheet.user) {
      return;
    }

    if (this.newTimesheet.user.locations.length === 1) {
      this.newTimesheet.location = this.newTimesheet.user.locations[0];
    }

    if (this.newTimesheet.user.positions.length === 1) {
      this.newTimesheet.position = this.newTimesheet.user.positions[0];
    }
  }

  async addTimeclockNote() {
    if (!this.TIME_CARD_NOTES_ENABLED || !this.event || !this.form.note?.$dirty) {
      return;
    }

    return await this.TimesheetsService.addTimeclockNote(this.event.id, { notes: this.note });
  }

  async removeTimeclockNote() {
    if (!this.TIME_CARD_NOTES_ENABLED || !this.event) {
      return;
    }

    return await this.TimesheetsService.addTimeclockNote(this.event.id, { notes: '' });
  }

  historyBack() {
    this.historyMode = false;
    if (!this.AUTOMATED_BREAKS_ENABLED) {
      this.modal.dismiss();
    }
  }
}

export default {
  bindings: {
    event: '<',
    modal: '<',
    readonly: '<?',
    initialMode: '<',
    defaultDate: '<?',
    editNoteModeOnly: '<',
  },
  templateUrl: editTimesheetsComponentUrl,
  controllerAs: 'ctrl',
  controller: EditTimesheetsCtrl,
};
